import { EmbedSettingsResponseDto, JobOfferEmbedDetailsShowingV2ResponseDto } from "@/lib/interfaces/embed";
import { useEffect, useMemo } from "react";
import type { IframeSettingsDesignDraft } from "../IframeSettingsPreviewContext";
import {
  buildEmbedJobDynamicCss,
  buildEmbedJobInjectedStyleContent,
  mergeEmbedVisualSettings,
} from "./embed-visual-settings";
const EMBED_JOB_STYLE_ID_PREFIX = "embed-job-combined-css";

export function useEmbedJobStyles({
  organizationId,
  jobId,
  isLiveSettingsPreview,
  previewDesign,
  embedBaseSettings,
  sourceJobPost,
  settingsPreview,
}: {
  organizationId: string;
  jobId: string;
  isLiveSettingsPreview: boolean;
  previewDesign?: IframeSettingsDesignDraft;
  embedBaseSettings: EmbedSettingsResponseDto | null;
  sourceJobPost: JobOfferEmbedDetailsShowingV2ResponseDto | undefined;
  /** When truthy, CSS is scoped to the Karriereportal preview root. */
  settingsPreview?: unknown;
}) {
  const styleElementId = `${EMBED_JOB_STYLE_ID_PREFIX}-${organizationId}-${isLiveSettingsPreview ? "preview" : jobId}`.replace(
    /[^a-zA-Z0-9-_]/g,
    "-",
  );

  const mergedVisual = useMemo(() => {
    const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : undefined;
    const fallbackColor = sourceJobPost?.organization.color ?? "#3182ce";

    if (isLiveSettingsPreview) {
      if (previewDesign) {
        return mergeEmbedVisualSettings(
          {
            color: previewDesign.color,
            customCss: previewDesign.customCss,
          },
          { fallbackColor },
        );
      }
      return mergeEmbedVisualSettings({}, { fallbackColor });
    }

    const base: Partial<EmbedSettingsResponseDto> = embedBaseSettings ?? {
      showImage: true,
    };
    return mergeEmbedVisualSettings(base, { urlParams, fallbackColor });
  }, [embedBaseSettings, isLiveSettingsPreview, previewDesign, sourceJobPost?.organization.color]);

  useEffect(() => {
    const dynamicCss = buildEmbedJobDynamicCss({
      color: mergedVisual.color,
    });
    const textContent = buildEmbedJobInjectedStyleContent(dynamicCss, mergedVisual.customCss, {
      scopeToPreviewRoot: Boolean(settingsPreview),
    });

    const existing = document.getElementById(styleElementId);
    if (existing) existing.remove();

    const styleElement = document.createElement("style");
    styleElement.id = styleElementId;
    styleElement.textContent = textContent;
    document.head.appendChild(styleElement);

    return () => {
      styleElement.remove();
    };
  }, [mergedVisual, settingsPreview, styleElementId]);

  return { mergedVisual };
}
